import { RankXPName } from "./rank";

export interface RebirthReward {
  label: string;
  value: string;
  /** Public asset path for the reward icon. */
  icon: string;
  /** Emphasized reward (e.g. the headline XP %). */
  highlight?: boolean;
}

/** Shared icon paths for rebirth rewards (existing app assets). */
export const REBIRTH_REWARD_ICONS = {
  xp: "/assets/imgs/rank-activation/star.png",
  killXp: "/assets/imgs/killskill/target.png",
  cash: "/assets/imgs/inventory/cashitem.png",
  bullets: "/assets/imgs/inventory/bulletitem.png",
  helperCredits: "/assets/imgs/inventory/helperitem.png",
  premium: "/assets/imgs/premium/badge.png",
  characterSam: "/assets/imgs/inventory/sam-bgsmall.png",
  characterFrank: "/assets/imgs/inventory/frank-bgsmall.png",
} as const;

export interface RebirthOption {
  id: number;
  name: string;
  subtitle: string;
  description: string;
  /** USD cost multiplier applied to the rank-based base cost. */
  costMultiplier: number;
  rewards: RebirthReward[];
}

/**
 * Rebirth cost is based on the dead player's rank (level).
 * First rank costs $50, every rank after adds $15 — applied to Option 1 (1x).
 * Option 2 = 1.5x and Option 3 = 2x of that base.
 */
export const REBIRTH_BASE_COST_USD = 50;
export const REBIRTH_COST_PER_RANK_USD = 15;
/** Highest rank index (Godfather) — costs are clamped to this. */
export const REBIRTH_MAX_RANK_INDEX = RankXPName.length - 1;

export const getRebirthBaseCostUsd = (rankIndex: number): number => {
  const safeRank = Math.max(
    0,
    Math.min(Math.floor(rankIndex || 0) - 1, REBIRTH_MAX_RANK_INDEX)
  );

  return REBIRTH_BASE_COST_USD + safeRank * REBIRTH_COST_PER_RANK_USD;
};

export const getRebirthOptionCostUsd = (
  option: RebirthOption,
  rankIndex: number
): number => Math.round(getRebirthBaseCostUsd(rankIndex) * option.costMultiplier);

/**
 * Rebirth tiers. Rewards are display-only for now — minting / granting will be
 * wired to the contract once it is deployed.
 */
export const REBIRTH_OPTIONS: RebirthOption[] = [
  {
    id: 0,
    name: "Light Rebirth",
    subtitle: "XP only",
    description:
      "The cheapest comeback. Recover most of your XP with a small cash and bullet boost. No asset mints.",
    costMultiplier: 1,
    rewards: [
      {
        label: "XP returned",
        value: "70%",
        icon: REBIRTH_REWARD_ICONS.xp,
        highlight: true,
      },
      { label: "Cash", value: "1,000,000", icon: REBIRTH_REWARD_ICONS.cash },
      { label: "Bullets", value: "10,000", icon: REBIRTH_REWARD_ICONS.bullets },
      {
        label: "Kill XP returned",
        value: "20%",
        icon: REBIRTH_REWARD_ICONS.killXp,
      },
    ],
  },
  {
    id: 1,
    name: "Medium Rebirth",
    subtitle: "XP + Assets",
    description:
      "Balanced recovery with extra cash, bullets, helper credits and a character mint.",
    costMultiplier: 1.5,
    rewards: [
      {
        label: "XP returned",
        value: "80%",
        icon: REBIRTH_REWARD_ICONS.xp,
        highlight: true,
      },
      {
        label: "Helper credits",
        value: "500",
        icon: REBIRTH_REWARD_ICONS.helperCredits,
      },
      { label: "Cash", value: "2,500,000", icon: REBIRTH_REWARD_ICONS.cash },
      {
        label: "Bullets",
        value: "100,000",
        icon: REBIRTH_REWARD_ICONS.bullets,
      },
      {
        label: "Bodyguard",
        value: "Sam Level 3",
        icon: REBIRTH_REWARD_ICONS.characterSam,
      },
      {
        label: "Kill XP returned",
        value: "40%",
        icon: REBIRTH_REWARD_ICONS.killXp,
      },
    ],
  },
  {
    id: 2,
    name: "Max Rebirth",
    subtitle: "Full comeback",
    description:
      "The ultimate revival. Maximum XP plus a premium subscription, a bodyguard and the largest asset bundle.",
    costMultiplier: 2,
    rewards: [
      {
        label: "XP returned",
        value: "90%",
        icon: REBIRTH_REWARD_ICONS.xp,
        highlight: true,
      },
      {
        label: "Helper credit mint",
        value: "1,000",
        icon: REBIRTH_REWARD_ICONS.helperCredits,
      },
      { label: "Cash", value: "5,000,000", icon: REBIRTH_REWARD_ICONS.cash },
      {
        label: "Premium subscription",
        value: "1 Month",
        icon: REBIRTH_REWARD_ICONS.premium,
      },
      {
        label: "Bullets",
        value: "250,000",
        icon: REBIRTH_REWARD_ICONS.bullets,
      },
      {
        label: "Bodyguard",
        value: "Frank Level 5",
        icon: REBIRTH_REWARD_ICONS.characterFrank,
      },
      {
        label: "Kill XP returned",
        value: "60%",
        icon: REBIRTH_REWARD_ICONS.killXp,
      },
    ],
  },
];
